/* Raiffeisen banki API — credential-szerkesztő (Beállítások → Bankszámlák → API kapcsolat).
 *
 * Külön fájlban, nem a misc.js-ben: az a fájl már 4300+ sor, és ez a panel önálló,
 * jól határolt felület.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠⚠ GÁBOR 2026-08-11-I VISSZAJELZÉSE — HÁROM VALÓS HIBA, ÉS A JAVÍTÁSUK
 *
 * Az első verzió minden mezőt egyforma, ÜRES inputként mutatott, akkor is, ha az
 * érték már be volt állítva (a titkot a backend soha nem adja vissza). Ez így nem
 * használható, és Gábor pontosan a három tünetet írta le:
 *
 * 1. „Azt kéred, töltsek ki 6 adatot, de közben azt mondod, egyeseket már
 *    kitöltöttél, amiket nem látok."
 *    → JAVÍTÁS: a beállított mezőnél NINCS input. Egy lezárt sor látszik
 *      (✓ név · dátum) + „Csere” gomb. Input CSAK ott van, ahol tényleg kell
 *      bevitel. Így a form önmagában megmutatja, mi hiányzik — nincs rejtett
 *      állapot, amit egy apró jelvényből kellene kiolvasni.
 *
 * 2. „Ha refreshelek, eltűnik, amit eddig beraktam.”
 *    → JAVÍTÁS: a piszkozat `sessionStorage`-ban él (készletenként külön
 *      kulccsal), tehát egy újratöltés NEM veszi el. Plusz `beforeunload`
 *      figyelmeztetés, ha van nem mentett bevitel. ⚠ SESSION-storage, nem local:
 *      a titkok ne éljék túl a böngésző-fül bezárását.
 *
 * 3. „Ha egy mező egy file tartalmát kéri, akkor ne szöveget kérjen, hanem a
 *    file-t magát, aztán vedd ki belőle a szöveget."
 *    → JAVÍTÁS: a PEM-mezőkön fájlválasztó (drag&drop is) van, ami beolvassa a
 *      fájlt. A beillesztés megmarad másodlagos útként (van, aki vágólapról jön),
 *      de nem az az elsődleges.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⚠ AMI VÁLTOZATLAN, mert nem UX-kérdés:
 *
 * • A titok-mezők WRITE-ONLY: nincs „szerkesztés”, csak „csere”. ⚠ 2026-09-05-ig a
 *   backend SEMMIT nem adott vissza (a hosszát sem); Gábor 2026-09-05-i döntése óta a
 *   nem-titkos mezők (technikai user, törzsszám, tanúsítvány) tisztán, a titkosak
 *   maszkolva jönnek (`masked`: első 4 + utolsó 4, 16 alatt csak az utolsó 4) — hogy a
 *   felhasználó lássa, a JÓ kulcs van-e bent. A maszkot a backend képzi.
 * • A mentés ELŐTT step-up (friss jelszó), nem a 403-ra reagálunk
 *   (`.claude/rules/munkamenet-auth.md` 8.). Indok: EZEKKEL A KULCSOKKAL UTALÁS
 *   INDUL — egy ellopott munkamenettel átírt api key/KID idegen technikai user
 *   nevében írhatna alá.
 * • Az üresen hagyott mező „ne változtass”, NEM törlés. Törlés = külön gomb,
 *   megerősítéssel.
 * • 2026-09-25 (C-D12): EGYETLEN Raiffeisen API-generáció van (`v1`). A régi
 *   generáció-jelvény, a „Melyik kulcskészletet szerkeszted?" választó és az eltérés-
 *   figyelmeztetés kivezetve; a készletet a cég környezete adja (`raiffeisen_sandbox_v1`
 *   / `raiffeisen_prod_v1`). A kérésekben nincs `generation` paraméter (a PUT 400-at adna).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, History, KeyRound, RotateCcw, Trash2, X } from 'lucide-react';
import { raiffeisenApi, freshRequest, sendFresh } from '../services/api';
import { Alert, Badge, Btn, Card, ConfirmDialog, DropButton,
         Spinner } from './UI';
import { useToast } from './Toast';
import { useStepUp } from '../contexts/StepUpContext';

/* Ezek fájlból jönnek (a portálról / a CSR-generálásból) → fájlválasztót kapnak. */
const FILE_FIELDS = {
  private_key: { accept: '.pem,.key,application/x-pem-file,text/plain',
                 want: 'PRIVATE KEY',
                 hint: 'raiffeisen_sandbox.pem' },
  certificate: { accept: '.pem,.crt,.cer,application/x-pem-file,text/plain',
                 want: 'CERTIFICATE',
                 hint: 'a portálról letöltött tanúsítvány' },
};

const fmtTs = (iso) => iso
  ? new Date(iso).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit' })
  : '—';

const LBL = { fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.06em' };

const BOX = {
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', padding: '0.45rem 0.65rem',
  color: 'var(--text)', fontSize: '0.85rem', outline: 'none', width: '100%',
};

/* ⚠ A piszkozat kulcsának ALAKJA szándékosan változatlan (`…:<env>:v1`, 2026-09-25):
 * így egy a kivezetés előtt elkezdett, mentetlen v1-piszkozat megmarad. A generáció
 * mindig `'v1'` (egyetlen generáció). */
const draftKey = (env, gen) => `raiffeisen-cred-draft:${env}:${gen}`;

/* A PEM első sora — visszaigazolásként megjelenítjük, hogy a felhasználó látja,
 * a JÓ fájlt választotta. ⚠ CSAK az első sor (`-----BEGIN …-----`): az nem titok,
 * a tartalom viszont igen, azt soha nem írjuk ki. */
const pemHead = (txt) => (txt || '').trim().split('\n')[0].trim();

// ═════════════════════════════════════════════════════════════════════════════

/* Card vagy beágyazott blokk — MODUL-SZINTŰ komponens (egy renderen belül definiált
 * wrapper minden renderen új típus volna, és a React újramountolná az inputokat). */
function CredWrap({ embedded, children }) {
  if (embedded) {
    return (
      <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <div style={{ ...LBL, marginBottom: 8 }}>Hitelesítő adatok (Pay Portál)</div>
        {children}
      </div>
    );
  }
  return (
    <Card title="Banki API hozzáférés"
          subtitle="A Raiffeisen Pay Portálról kinyert credentialek. Titkosítva, cégenként tárolva — a titkos mezőkből csak az eleje és a vége látszik, ezért a beállított mezőknél „Csere” gomb van, nem szerkeszthető input.">
      {children}
    </Card>
  );
}

/** Beállított mező: lezárt sor, input NÉLKÜL. Ez a 1. javítás lényege. */
function SetRow({ f, onReplace, onClear, busy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '0.5rem 0.65rem', background: 'rgba(34,197,94,0.06)',
                  border: '1px solid rgba(34,197,94,0.25)',
                  borderRadius: 'var(--radius)' }}>
      <Check size={15} style={{ color: '#22c55e', flexShrink: 0 }}/>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{f.label}</div>
        {/* 2026-09-05 (Gábor): a nem-titkos mező tisztán, a titkos maszkolva (első 4 +
            utolsó 4, 16 alatt csak az utolsó 4) — a maszkot a backend képzi. */}
        {f.masked && (
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.76rem',
                        color: f.secret ? 'var(--text-muted)' : 'var(--text)', wordBreak: 'break-all' }}
               title={f.secret ? 'Titkos mező — csak az eleje és a vége látszik' : undefined}>
            {f.masked}
          </div>
        )}
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
          beállítva: {fmtTs(f.updated_at)}
          {f.version_label ? ` · ${f.version_label}` : ''}
          {f.history_count > 0 ? ` · ${f.history_count} korábbi verzió` : ''}
        </div>
      </div>
      <Btn size="sm" variant="secondary" onClick={() => onReplace(f.field)} disabled={busy}>
        <RotateCcw size={13}/> Csere
      </Btn>
      {/* ⚠ `outline`, nem `ghost`: a ghost keret és háttér nélküli, halvány — nem
          látszik gombnak (2026-08-11-i visszajelzés a „Mégse"-nél). Egy TÖRLŐ
          műveletnél különösen fontos, hogy kattinthatónak látszódjon. */}
      <Btn size="sm" variant="outline" onClick={() => onClear(f)} disabled={busy}
           title="A titok törlése (minden verzió)">
        <Trash2 size={13}/>
      </Btn>
    </div>
  );
}

/** Bevitelt igénylő mező. Fájl-mezőnél fájlválasztó az elsődleges út. */
function InputRow({ f, value, onChange, onCancelReplace, fileErr, onFile }) {
  const fileCfg = FILE_FIELDS[f.field];
  const head = fileCfg && value ? pemHead(value) : '';
  const headOk = head && head.includes(fileCfg.want);
  /* CSERE-állapot: a mező MÁR be van állítva, csak a felhasználó újat ad meg.
   * ⚠ Ez vizuálisan is elkülönül (borostyán keret), mert Gábor 2026-08-11-én
   * pontosan itt akadt el: a „Csere" után a sor átkerült a Kitöltendő blokkba egy
   * ÜRES jelszó-mezővel, és ez úgy olvasódott, hogy az érték ELVESZETT — pedig a
   * tárolt érték érintetlen. Egy üres input + halvány szöveg nem elég jelzés. */
  const isReplace = !!f.is_set;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5,
                  ...(isReplace ? {
                    padding: '0.6rem 0.7rem',
                    background: 'rgba(245,158,11,0.06)',
                    border: '1px solid rgba(245,158,11,0.35)',
                    borderRadius: 'var(--radius)',
                  } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={LBL}>{f.label}</span>
        {/* ⚠ Az „opcionális" jelölés is KI VAN ÍRVA, nem a „kötelező" badge
            HIÁNYÁRA bízzuk. Egy 6 mezős formnál a felhasználónak látnia kell,
            mit hagyhat ki — a hiányzó jelvényből ezt kikövetkeztetni munka. */}
        <Badge variant={f.required ? 'warning' : 'default'}>
          {f.required ? 'kötelező' : 'opcionális'}
        </Badge>
        {isReplace && (
          <>
            <Badge variant="pending">csere folyamatban</Badge>
            {/* ⚠ `secondary`, NEM `ghost`: a ghost átlátszó hátterű, keret nélküli,
                halvány szövegű — technikailag ott van, de NEM LÁTSZIK gombnak.
                2026-08-11-én Gábor emiatt jelezte, hogy „nincs mégse gomb". */}
            <Btn size="sm" variant="secondary"
                 onClick={() => onCancelReplace(f.field)}>
              <X size={13}/> Mégse (marad a mostani)
            </Btn>
          </>
        )}
      </div>

      {/* ⚠ A LEGFONTOSABB mondat ebben az állapotban: a mostani érték ÉL, amíg nem
          mentesz újat. E nélkül az üres input „elvesztettem"-ként olvasódik. */}
      {isReplace && (
        <div style={{ fontSize: '0.75rem', color: '#fcd34d' }}>
          A mostani érték <b>érvényben marad</b> (beállítva: {fmtTs(f.updated_at)}) —
          csak akkor cserélődik, ha ide újat írsz <b>és mentesz</b>. A régi verzió a
          történetben megmarad.
        </div>
      )}

      {fileCfg ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <DropButton
              label="Fájl kiválasztása" hint={fileCfg.hint}
              accept={fileCfg.accept} multiple={false}
              onFiles={(files) => onFile(f.field, files)}/>
            {/* Visszaigazolás: CSAK a PEM első sora, a tartalom soha. */}
            {head && (
              <span style={{ fontSize: '0.75rem',
                             color: headOk ? '#22c55e' : 'var(--danger)',
                             fontFamily: 'ui-monospace, monospace' }}>
                {headOk ? '✓ ' : '⚠ '}{head}
              </span>
            )}
          </div>
          {fileErr && (
            <span style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{fileErr}</span>
          )}
          <details>
            <summary style={{ fontSize: '0.72rem', color: 'var(--text-muted)',
                              cursor: 'pointer' }}>
              vagy beillesztés vágólapról
            </summary>
            <textarea
              rows={4} spellCheck={false} value={value} autoComplete="off"
              placeholder="-----BEGIN …-----"
              onChange={(e) => onChange(f.field, e.target.value)}
              style={{ ...BOX, marginTop: 5, fontFamily: 'ui-monospace, monospace',
                       fontSize: '0.72rem', resize: 'vertical' }}/>
          </details>
        </div>
      ) : (
        <input
          // ⚠ `type="password"` a titkos mezőkön: a felület fölött álló ember ne
          //   lássa a beírt kulcsot.
          type={f.secret ? 'password' : 'text'}
          value={value} autoComplete="off" spellCheck={false}
          onChange={(e) => onChange(f.field, e.target.value)}
          style={BOX}/>
      )}
      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{f.hint}</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════

/* `environment` opcionális: ha nincs megadva, az AKTÍV környezetet használjuk.
 * ⚠ Nem hardkódolt 'sandbox' a default — élesben az csendben a teszt-készletet
 * szerkesztené, miközben a felhasználó azt hinné, az éles kulcsokat írja át. */
/* `embedded` (2026-09-05): Card nélkül, a Raiffeisen asset-sor alatt renderel — egy
 * bankkapcsolat egy helyen, nem külön „Banki API hozzáférés" kártyán. */
export default function RaiffeisenCredentials({ environment: envProp, embedded = false }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { requireFreshStepUp } = useStepUp();

  const [confirm, setConfirm] = useState(null);
  const [draft, setDraft] = useState({});
  /* Melyik BEÁLLÍTOTT mezőt akarja cserélni — ezeknél megjelenik az input. */
  const [replacing, setReplacing] = useState({});
  const [fileErr, setFileErr] = useState({});
  const [label, setLabel] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const { data: env = {} } = useQuery({
    queryKey: ['raiffeisen-env-status'], queryFn: raiffeisenApi.envStatus });
  const environment = envProp || env.environment;

  const { data: creds, isLoading } = useQuery({
    queryKey: ['raiffeisen-credentials', environment],
    queryFn: () => raiffeisenApi.credentials({ environment }),
    enabled: !!environment,
  });
  const { data: hist } = useQuery({
    queryKey: ['raiffeisen-cred-history', environment],
    queryFn: () => raiffeisenApi.credentialHistory({ environment }),
    enabled: showHistory && !!environment,
  });

  // ── 2. JAVÍTÁS: a piszkozat túléli az újratöltést ─────────────────────────
  const dkey = environment ? draftKey(environment, 'v1') : null;

  useEffect(() => {
    if (!dkey) return;
    try {
      const raw = sessionStorage.getItem(dkey);
      const saved = raw ? JSON.parse(raw) : {};
      setDraft(saved.draft || {});
      setReplacing(saved.replacing || {});
      setLabel(saved.label || '');
    } catch {
      setDraft({}); setReplacing({}); setLabel('');
    }
  }, [dkey]);

  useEffect(() => {
    if (!dkey) return;
    const any = Object.values(draft).some((v) => (v || '').trim() !== '');
    // ⚠ Csak akkor írunk, ha VAN mit — különben egy üres piszkozat felülírná a
    //   másik fülön épp beírt értéket.
    if (any || label || Object.keys(replacing).length) {
      sessionStorage.setItem(dkey, JSON.stringify({ draft, replacing, label }));
    } else {
      sessionStorage.removeItem(dkey);
    }
  }, [dkey, draft, replacing, label]);

  const filled = useMemo(
    () => Object.entries(draft).filter(([, v]) => (v || '').trim() !== ''),
    [draft]);

  /* Ha van nem mentett bevitel, a lap elhagyása/újratöltése figyelmeztet.
   * ⚠ A sessionStorage már megvédi az adatot; ez azért kell, mert a felhasználó
   *   azt HISZI, hogy elveszik — a néma újratöltés bizonytalanságot szül. */
  useEffect(() => {
    if (!filled.length) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [filled.length]);

  const clearDraft = useCallback(() => {
    setDraft({}); setReplacing({}); setLabel(''); setFileErr({});
    if (dkey) sessionStorage.removeItem(dkey);
  }, [dkey]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['raiffeisen-credentials'] });
    qc.invalidateQueries({ queryKey: ['raiffeisen-cred-history'] });
    qc.invalidateQueries({ queryKey: ['raiffeisen-env-status'] });
  };

  const saveMut = useMutation({
    // 2026-09-24: a banki hozzáférés a pénz útja → friss, erre a kérésre kötött passkey; a
    // mutáció a `freshRequest` leírót küldi, ugyanazt, amire a passkey szól.
    mutationFn: (req) => sendFresh(req),
    onSuccess: (d) => {
      clearDraft();
      const names = (d.written || []).map(
        (f) => creds?.fields?.find((x) => x.field === f)?.label || f);
      toast.success(names.length
        ? `Mentve: ${names.join(', ')}`
        // ⚠ Változatlan értékre a backend NEM csinál új verziót — ezt ki kell
        //   írni, különben a felhasználó azt hiszi, a mentés nem működött.
        : 'Nem változott semmi (a beírt érték azonos a tárolttal).');
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A mentés nem sikerült.'),
  });

  const clearMut = useMutation({
    mutationFn: (req) => sendFresh(req),
    onSuccess: () => { toast.success('Törölve.'); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A törlés nem sikerült.'),
  });

  const onChange = (field, v) => setDraft((d) => ({ ...d, [field]: v }));

  // ── 3. JAVÍTÁS: fájl → tartalom, itt a felületen ──────────────────────────
  const onFile = async (field, files) => {
    const file = files && files[0];
    if (!file) return;
    setFileErr((s) => ({ ...s, [field]: null }));
    try {
      const txt = await file.text();
      const want = FILE_FIELDS[field].want;
      const head = pemHead(txt);
      if (!head.startsWith('-----BEGIN')) {
        // ⚠ Itt fogjuk meg a DER/bináris fájlt: a backend is elutasítaná, de a
        //   felhasználónak a fájlválasztás pillanatában kell megtudnia.
        setFileErr((s) => ({ ...s, [field]:
          `A(z) „${file.name}” nem PEM-fájl (nem -----BEGIN sorral kezdődik). `
          + 'Ha DER/bináris a tanúsítvány, a portálon kérj PEM (Base64) formátumot.' }));
        return;
      }
      if (!head.includes(want)) {
        // A leggyakoribb emberi hiba: a cert és a kulcs felcserélése.
        setFileErr((s) => ({ ...s, [field]:
          `Ez „${head.replace(/-----|BEGIN/g, '').trim()}”, pedig „${want}” kell. `
          + 'Valószínűleg felcserélted a privát kulcsot és a tanúsítványt.' }));
        return;
      }
      onChange(field, txt);
      toast.success(`Beolvasva: ${file.name}`);
    } catch {
      setFileErr((s) => ({ ...s, [field]: 'A fájl nem olvasható.' }));
    }
  };

  const onSave = async () => {
    if (!filled.length) { toast.info('Nincs kitöltött mező.'); return; }
    // Lásd a fejlécet: a művelet ELŐTT kérdezünk, nem a 403-ra reagálunk.
    const req = freshRequest('PUT /raiffeisen/credentials', { body: {
      ...Object.fromEntries(filled),
      environment,
      label: label.trim() || null,
    } });
    if (!await requireFreshStepUp('A banki hozzáférés módosítása előtt ', [req])) return;
    saveMut.mutate(req);
  };

  const onClear = (f) => setConfirm({
    title: 'Titok törlése', danger: true, confirmLabel: 'Törlés',
    message: `Biztosan törlöd a(z) „${f.label}” mezőt? A törlés MINDEN verziót `
           + 'elvisz, és a banki hívások (utalás, egyenleg, kivonat) enélkül '
           + 'nem működnek, amíg újra be nem állítod.',
    action: async () => {
      const req = freshRequest('DELETE /raiffeisen/credentials',
        { query: { fields: f.field, environment } });
      if (!await requireFreshStepUp('A banki hozzáférés módosítása előtt ', [req])) return;
      clearMut.mutate(req);
    },
  });

  if (isLoading || !environment) return <Spinner/>;

  const fields = creds?.fields || [];
  /* Bevitelt igényel: nincs beállítva, VAGY a felhasználó cserét kért. */
  /* HÁROM állapot, három szekció — szándékosan nem kettő.
   * ⚠ Egy csere-sor NEM „kitöltendő": a mezőnek VAN értéke, csak a felhasználó
   *   újat ad meg (és a Mégse-vel visszaléphet). Ha egy közös „Kitöltendő (4)"
   *   számlálóba esne, a felirat azt sugallná, hogy négy dolog hiányzik — pedig
   *   csak három, és az egyik csak csere. */
  const missing = fields.filter((f) => !f.is_set);
  const inReplace = fields.filter((f) => f.is_set && replacing[f.field]);
  const done = fields.filter((f) => f.is_set && !replacing[f.field]);
  const cancelReplace = (field) => {
    setReplacing((r) => { const n = { ...r }; delete n[field]; return n; });
    setDraft((d) => { const n = { ...d }; delete n[field]; return n; });
    setFileErr((s) => { const n = { ...s }; delete n[field]; return n; });
  };
  const reqTotal = fields.filter((f) => f.required).length;
  const reqSet = fields.filter((f) => f.required && f.is_set).length;

  return (
    <CredWrap embedded={embedded}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          készlet: <code>{creds?.service}</code>
          {' · '}kötelező mezők: <b>{reqSet}/{reqTotal}</b>
        </div>

        {filled.length > 0 && (
          <Alert type="info">
            <b>{filled.length} nem mentett mező.</b> A bevitel újratöltés után is
            megmarad (a böngésző-fül bezárásáig), de a banki hívások csak mentés
            után használják.
          </Alert>
        )}

        {/* ── 1. Amit MEG KELL adni (nincs értéke) ───────────────────────── */}
        {missing.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={LBL}>Kitöltendő ({missing.length})</div>
            {missing.map((f) => (
              <InputRow key={f.field} f={f} value={draft[f.field] || ''}
                        onChange={onChange} onFile={onFile}
                        fileErr={fileErr[f.field]}
                        onCancelReplace={cancelReplace}/>
            ))}
          </div>
        )}

        {/* ── 2. Csere folyamatban (VAN értéke, de újat adnál meg) ────────── */}
        {inReplace.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={LBL}>Csere folyamatban ({inReplace.length})</div>
            {inReplace.map((f) => (
              <InputRow key={f.field} f={f} value={draft[f.field] || ''}
                        onChange={onChange} onFile={onFile}
                        fileErr={fileErr[f.field]}
                        onCancelReplace={cancelReplace}/>
            ))}
          </div>
        )}

        {/* ── Ami MÁR megvan (input NÉLKÜL — ez az 1. javítás) ───────────── */}
        {done.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={LBL}>Beállítva ({done.length})</div>
            {done.map((f) => (
              <SetRow key={f.field} f={f} busy={clearMut.isPending}
                      onReplace={(field) => setReplacing((r) => ({ ...r, [field]: true }))}
                      onClear={onClear}/>
            ))}
          </div>
        )}

        {missing.length === 0 && inReplace.length === 0 && (
          <Alert type="success">
            Minden mező be van állítva ezen a készleten. Cseréhez használd a
            „Csere” gombot a megfelelő sorban.
          </Alert>
        )}

        {creds && !creds.complete && (
          <Alert type="error">
            Hiányzó <b>kötelező</b> mező:{' '}
            <b>{creds.missing_required.map(
              (f) => fields.find((x) => x.field === f)?.label || f).join(', ')}</b>.
            {' Enélkül a banki hívások nem működnek.'}
          </Alert>
        )}

        {/* ── Mentés ─────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap',
                      borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1,
                          minWidth: 200 }}>
            <span style={LBL}>Verzió-címke (opcionális)</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="pl. 2026-08 új portál-user"
                   style={BOX} autoComplete="off"/>
          </label>
          <Btn onClick={onSave} disabled={saveMut.isPending || !filled.length}>
            <KeyRound size={14}/>
            {saveMut.isPending ? 'Mentés…' : `Mentés (${filled.length})`}
          </Btn>
          {filled.length > 0 && (
            <Btn variant="ghost" onClick={clearDraft} disabled={saveMut.isPending}>
              <X size={14}/> Bevitel elvetése
            </Btn>
          )}
          <Btn variant="ghost" onClick={() => setShowHistory((s) => !s)}>
            <History size={14}/> {showHistory ? 'Történet elrejtése' : 'Verzió-történet'}
          </Btn>
        </div>

        {/* ── Verzió-történet (ÉRTÉK NÉLKÜL) ─────────────────────────────── */}
        {showHistory && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ ...LBL, marginBottom: 6 }}>Verzió-történet — {creds?.service}</div>
            {!hist?.rows?.length ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Még nincs rögzített verzió.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                                fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '4px 8px' }}>Mező</th>
                      <th style={{ padding: '4px 8px' }}>Állapot</th>
                      <th style={{ padding: '4px 8px' }}>Létrehozva</th>
                      <th style={{ padding: '4px 8px' }}>Leváltva</th>
                      <th style={{ padding: '4px 8px' }}>Címke</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.rows.map((r) => (
                      <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 8px' }}>
                          {fields.find((x) => x.field === r.field)?.label || r.field}
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          {r.is_current
                            ? <Badge variant="success">aktuális</Badge>
                            : <Badge variant="default">korábbi</Badge>}
                        </td>
                        <td style={{ padding: '4px 8px' }}>{fmtTs(r.created_at)}</td>
                        <td style={{ padding: '4px 8px' }}>{fmtTs(r.superseded_at)}</td>
                        <td style={{ padding: '4px 8px' }}>{r.label || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 6 }}>
              A történet szándékosan <b>nem tartalmaz értéket</b> (még a hosszát sem).
              A mentés nem írja felül a korábbi verziót, csak leváltja — így egy
              elrontott kulcscsere visszakövethető.
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const a = confirm?.action; setConfirm(null); a && a(); }}/>
    </CredWrap>
  );
}
